import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {Title} from '@angular/platform-browser';
import {DataService} from '../../../../../Services/data.service';
import {DateFilterService} from '../../../../../Services/dateFilter.service';
import {EmailModel} from '../../../../../DataModels/email-model';
import {BsToastService} from '../../../../../Services/bs-toast-service';
import {Modal} from 'bootstrap';
import {DataExportService} from '../../../../../Services/dataExport.service';
import {ReportPrintService} from '../../../../../Services/report-print.service';

@Component({
  selector: 'app-workorder-report',
  templateUrl: './workorder-report.component.html',
  styleUrls: ['./workorder-report.component.css'],
  standalone: false
})
export class WorkorderReportComponent implements OnInit, AfterViewInit {
  @ViewChild('ExcelExport', {static: false}) ExcelExport: ElementRef;
  @ViewChild('ReportOutput', {static: false}) ReportOutput: ElementRef;

  reportDataToSendInEmail: EmailModel = new EmailModel();

  vm: any = {
    report: [],
    buttonsDisabled: false,
    showFilters: false,
    exportToExcel: true,
    toDateSelect: [],
    fromDateSelect: [],
    DataFormat: 'MM/DD/YYYY',
    showSpecificDates: false,
    fromDateError: false,
    toDateError: false,
    dateError: false,
    request: {
      assigned_user_id: null,
      company_id: null,
      entity_id: null,
      status: null,
      priority: null,
      billing_status: null,
      type_id: null,
      category_id: null,
      fromDate: null,
      toDate: null,
    },
    users: [],
    company: [],
    entity: [],
    woTypes: [],
    woCategories: [],
  };

  buttonConfig: any = {
    reportFormat: false,
    print: false,
    printReport: true,
    email: true,
    update: true,
    exportExcel: true,
    close: true,
  };

  noRecordsFound = false;

  // Summary totals computed after load
  summary: any = {
    total: 0, open: 0, dispatched: 0, inProgress: 0, completed: 0, invoiced: 0,
    unbilled: 0, partiallyBilled: 0, billed: 0,
    totalHours: 0, totalDistance: 0, totalMaterials: 0,
    totalBilledHours: 0, totalBilledTravel: 0, totalBilledMaterials: 0,
  };

  today = new Date();
  statuses = ['Open', 'Dispatched', 'In Progress', 'Completed', 'Invoiced'];
  priorities = ['Critical', 'High', 'Normal', 'Low'];
  billingStatuses = ['unbilled', 'partially_billed', 'billed', 'no_charge'];

  // track which WO rows are expanded
  expanded: Set<string> = new Set();

  constructor(
    private titleService: Title,
    private tableDataService: DataService,
    private dateFilter: DateFilterService,
    private toastService: BsToastService,
    public excelExporter: DataExportService,
    private reportPrint: ReportPrintService
  ) {
  }

  ngOnInit(): void {
    this.titleService.setTitle('Work Order Report');
    this.newDateFilter(this.vm.request.toDate, this.vm.request.fromDate);
    this.loadLookups();
  }

  ngAfterViewInit() {
  }

  newDateFilter(toDate: any, fromDate: any) {
    const ret = this.dateFilter.DateFilterInit(toDate, fromDate);
    this.vm.fromDateSelect = ret.fromDateSelect;
    this.vm.toDateSelect = ret.toDateSelect;
  }

  loadLookups() {
    this.tableDataService.getDataWithParams('sys_user', 'pageNumber=0&pageSize=999999').subscribe({
      next: (r: any) => {
        this.vm.users = r?.Data ?? r?.data ?? r ?? [];
      }
    });
    this.tableDataService.getDataWithParams('company', 'pageNumber=0&pageSize=999999').subscribe({
      next: (r: any) => {
        this.vm.company = r?.Data ?? r?.data ?? r ?? [];
      }
    });
    this.tableDataService.getDataWithParams('entity', 'pageNumber=0&pageSize=999999').subscribe({
      next: (r: any) => {
        this.vm.entity = r?.Data ?? r?.data ?? r ?? [];
      }
    });
    this.tableDataService.getData('wo_type').subscribe({
      next: (r: any) => {
        this.vm.woTypes = Array.isArray(r) ? r : (r?.Data ?? r?.data ?? []);
      }
    });
    this.tableDataService.getData('wo_category').subscribe({
      next: (r: any) => {
        this.vm.woCategories = Array.isArray(r) ? r : (r?.Data ?? r?.data ?? []);
      }
    });
  }

  update() {
    this.noRecordsFound = false;
    this.vm.report = [];
    this.expanded.clear();
    this.vm.fromDateError = false;
    this.vm.toDateError = false;

    const hasFrom = !!this.vm.request.fromDate;
    const hasTo = !!this.vm.request.toDate;
    if (!hasFrom || !hasTo) {
      this.vm.fromDateError = !hasFrom;
      this.vm.toDateError = !hasTo;
      this.toastService.showWarningToast('Must select From and To dates.');
      return;
    }

    const fromDate = new Date(this.vm.request.fromDate);
    const toDate = new Date(this.vm.request.toDate);
    if (toDate < fromDate) {
      this.toastService.showWarningToast('Please select a valid date range.');
      return;
    }

    const params: Record<string, any> = {
      fromDate: this.dateFilter.GetDateFormat(this.vm.request.fromDate),
      toDate: this.dateFilter.GetDateFormat(this.vm.request.toDate),
    };
    if (this.vm.request.company_id) {
      params['company_id'] = this.vm.request.company_id;
    }
    if (this.vm.request.entity_id) {
      params['entity_id'] = this.vm.request.entity_id;
    }
    if (this.vm.request.assigned_user_id) {
      params['assigned_user_id'] = this.vm.request.assigned_user_id;
    }
    if (this.vm.request.status) {
      params['status'] = this.vm.request.status;
    }
    if (this.vm.request.priority) {
      params['priority'] = this.vm.request.priority;
    }
    if (this.vm.request.billing_status) {
      params['billing_status'] = this.vm.request.billing_status;
    }
    if (this.vm.request.type_id) {
      params['type_id'] = this.vm.request.type_id;
    }
    if (this.vm.request.category_id) {
      params['category_id'] = this.vm.request.category_id;
    }

    const qs = this.encodeQueryData(params);
    this.vm.buttonsDisabled = true;

    this.tableDataService.getData('WorkOrder', 'all?' + qs).subscribe({
      next: (result: any) => {
        const data: any[] = Array.isArray(result) ? result : (result?.Data ?? result?.data ?? []);
        this.vm.report = data;
        this.noRecordsFound = data.length === 0;
        this.computeSummary(data);
        this.vm.buttonsDisabled = false;
      },
      error: () => {
        this.vm.report = [];
        this.vm.buttonsDisabled = false;
      }
    });
  }

  computeSummary(data: any[]) {
    const s = {
      total: data.length, open: 0, dispatched: 0, inProgress: 0, completed: 0, invoiced: 0,
      unbilled: 0, partiallyBilled: 0, billed: 0, noCharge: 0,
      totalHours: 0, totalDistance: 0, totalMaterials: 0,
      totalBilledHours: 0, totalBilledTravel: 0, totalBilledMaterials: 0,
    };
    for (const r of data) {
      const h = r.wo_header ?? r;
      switch (h.status) {
        case 'Open':
          s.open++;
          break;
        case 'Dispatched':
          s.dispatched++;
          break;
        case 'In Progress':
          s.inProgress++;
          break;
        case 'Completed':
          s.completed++;
          break;
        case 'Invoiced':
          s.invoiced++;
          break;
      }
      switch (h.billing_status) {
        case 'unbilled':
          s.unbilled++;
          break;
        case 'partially_billed':
          s.partiallyBilled++;
          break;
        case 'billed':
          s.billed++;
          break;
        case 'no_charge':
          s.noCharge++;
          break;
      }
      for (const hr of (r.wo_hours ?? [])) {
        s.totalHours += +(hr.hours ?? 0);
        if (hr.billable !== false) {
          s.totalBilledHours += +(hr.billed_amount ?? (hr.hours ?? 0) * (hr.billing_rate ?? 0));
        }
      }
      for (const tr of (r.wo_travel ?? [])) {
        s.totalDistance += +(tr.distance ?? 0);
        if (tr.billable !== false) {
          s.totalBilledTravel += +(tr.billed_amount ?? (tr.distance ?? 0) * (tr.rate_per_unit ?? 0));
        }
      }
      for (const it of (r.wo_items ?? [])) {
        s.totalMaterials += +(it.quantity ?? 0) * +(it.unit_price ?? it.price ?? 0);
        if (it.billable !== false) {
          s.totalBilledMaterials += +(it.quantity ?? 0) * +(it.unit_price ?? it.price ?? 0);
        }
      }
    }
    this.summary = s;
  }

  userName(id: string | null): string {
    if (!id) {
      return '—';
    }
    return this.vm.users.find((u: any) => u.id === id)?.name ?? id;
  }

  typeName(id: string | null): string {
    if (!id) {
      return '';
    }
    return this.vm.woTypes.find((t: any) => t.id === id)?.name ?? '';
  }

  categoryName(id: string | null): string {
    if (!id) {
      return '';
    }
    return this.vm.woCategories.find((c: any) => c.id === id)?.name ?? '';
  }

  statusBadge(status: string): string {
    switch (status) {
      case 'Open':
        return 'bg-primary';
      case 'Dispatched':
        return 'bg-warning text-dark';
      case 'In Progress':
        return 'bg-info text-dark';
      case 'Completed':
        return 'bg-success';
      case 'Invoiced':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  }

  billingBadge(bs: string): string {
    switch (bs) {
      case 'unbilled':
        return 'bg-warning text-dark';
      case 'partially_billed':
        return 'bg-info text-dark';
      case 'billed':
        return 'bg-success';
      case 'no_charge':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  }

  billingLabel(bs: string): string {
    switch (bs) {
      case 'unbilled':
        return 'Unbilled';
      case 'partially_billed':
        return 'Partial';
      case 'billed':
        return 'Billed';
      case 'no_charge':
        return 'No Charge';
      default:
        return bs ?? '';
    }
  }

  priorityBadge(p: string): string {
    switch (p) {
      case 'Critical':
        return 'bg-danger';
      case 'High':
        return 'bg-warning text-dark';
      case 'Normal':
        return 'bg-primary';
      case 'Low':
        return 'bg-secondary';
      default:
        return 'bg-light text-dark';
    }
  }

  toggleExpand(id: string) {
    if (this.expanded.has(id)) {
      this.expanded.delete(id);
    } else {
      this.expanded.add(id);
    }
  }

  isExpanded(id: string): boolean {
    return this.expanded.has(id);
  }

  expandAll() {
    this.vm.report.forEach((r: any) => this.expanded.add(r.wo_header?.id ?? r.id));
  }

  collapseAll() {
    this.expanded.clear();
  }

  woHours(r: any): number {
    return (r.wo_hours ?? []).reduce((s: number, h: any) => s + +(h.hours ?? 0), 0);
  }

  woMaterialsTotal(r: any): number {
    return (r.wo_items ?? []).reduce((s: number, i: any) => s + +(i.quantity ?? 0) * +(i.unit_price ?? i.price ?? 0), 0);
  }

  woTravelTotal(r: any): number {
    return (r.wo_travel ?? []).reduce((s: number, t: any) => s + +(t.distance ?? 0) * +(t.rate_per_unit ?? 0), 0);
  }

  clear() {
    this.vm.request = {
      assigned_user_id: null, company_id: null, entity_id: null,
      status: null, priority: null, billing_status: null,
      type_id: null, category_id: null, fromDate: null, toDate: null,
    };
    this.vm.report = [];
    this.expanded.clear();
    this.noRecordsFound = false;
  }

  close() {
    window.close();
  }

  setEmailData() {
    const previousExpanded = new Set(this.expanded);
    this.expandAll();
    setTimeout(() => {
      const emailDocObj = {} as EmailModel;
      emailDocObj.print_orientation = '2';
      emailDocObj.convert_attachment_to = 'pdf';
      emailDocObj.fromEmail = '';
      emailDocObj.fromName = 'AcctXErp';
      emailDocObj.header_id = '';
      emailDocObj.toEmail = '';
      emailDocObj.body = 'Please see the attached Work Order Report.<br><br>Best regards,<br><br>AcctXErp<br><br>';
      emailDocObj.Subject = 'AcctXErp Work Order Report';
      try {
        const raw = this.ReportOutput?.nativeElement?.outerHTML ?? this.buildPrintHtml(true);
        const content = this.reportPrint.cleanContentHtml(raw);
        const from = this.vm?.request?.fromDate ?? '';
        const to = this.vm?.request?.toDate ?? '';
        const meta: { label: string; value: string }[] = [];
        if (from || to) {
          meta.push({label: 'Period', value: from && to ? `${from} to ${to}` : (from || to)});
        }
        emailDocObj.pdfHtml = this.reportPrint.buildHtml({
          title: 'Work Order Report',
          contentHtml: content,
          meta,
          orientation: 'landscape'
        });
      } catch {
        emailDocObj.pdfHtml = this.ExcelExport?.nativeElement?.outerHTML ?? '';
      }
      emailDocObj.activity_message = 'Work Order Report';
      const now = new Date();
      emailDocObj.FileName = `Work_Order_Report_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.pdf`;
      this.reportDataToSendInEmail = emailDocObj;
      this.expanded = previousExpanded;
      const el = document.getElementById('sendEmailModal') as HTMLElement;
      if (el) {
        new Modal(el).show();
      }
    }, 50);
  }

  printReport() {
    // Save current expanded state, expand all so detail rows are in the DOM, then restore
    const previousExpanded = new Set(this.expanded);
    this.expandAll();
    setTimeout(() => {
      try {
        const raw = this.ReportOutput?.nativeElement?.outerHTML ?? this.buildPrintHtml(false);
        const content = this.reportPrint.cleanContentHtml(raw);
        const from = this.vm?.request?.fromDate ?? '';
        const to = this.vm?.request?.toDate ?? '';
        const meta: { label: string; value: string }[] = [];
        if (from || to) {
          meta.push({label: 'Period', value: from && to ? `${from} to ${to}` : (from || to)});
        }
        const html = this.reportPrint.buildHtml({title: 'Work Order Report', contentHtml: content, meta, orientation: 'landscape'});
        this.reportPrint.printInIframe(html);
      } catch {
      }
      this.expanded = previousExpanded;
    }, 50);
  }

  exportToExcel() {
    if (!this.ExcelExport?.nativeElement) {
      return;
    }
    const now = new Date();
    const fn = `Work_Order_Report_${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}.xlsx`;
    this.excelExporter.exportToExcel(this.ExcelExport, fn, 'Work Order Report');
  }

  private buildPrintHtml(forEmail: boolean = false): string {
    const raw = (this.ExcelExport?.nativeElement?.outerHTML ?? document.getElementById('excel-export')?.outerHTML ?? '').toString();
    const content = this.reportPrint.cleanContentHtml(raw);
    const from = this.vm?.request?.fromDate ?? '';
    const to = this.vm?.request?.toDate ?? '';
    const meta: { label: string; value: string }[] = [];
    if (from || to) {
      meta.push({label: 'Period', value: from && to ? `${from} to ${to}` : (from || to)});
    }
    return this.reportPrint.buildHtml({title: 'Work Order Report', contentHtml: content, meta, orientation: 'landscape'});
  }

  private encodeQueryData(data: Record<string, any>): string {
    return Object.entries(data)
      .filter(([, v]) => v !== undefined && v !== null && v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
  }
}
